import type { FrequencyType } from '../types/global'

import useLocalStorage from './useLocalStorage'

export default function useFrequencyType() {
  const { t } = useI18n()
  const { frequencyTypesSegments } = useLocalStorage()

  const bgColors = {
    NEW: 'bg-blue-100',
    VIP: 'bg-yellow-100',
    OCCASIONAL: 'bg-stone-200',
    AT_RISK: 'bg-red-100'
  }

  const hexaColors = {
    NEW: '#008ff8',
    VIP: '#ffb92a',
    OCCASIONAL: '#475569',
    AT_RISK: '#fd2138'
  }

  const getBgColorBySlug = (slug: string): string => {
    return bgColors[slug] || ''
  }

  const getHexaColorBySlug = (slug: string): string => {
    return hexaColors[slug] || ''
  }

  const formattedFrequencyRule = (rule) => {
    const feminineMap = {
      checkin_count: false,
      average_checkin_count: false,
      last_valid_checkin: true,
      days_since_register: true
    }
    const attributeMap = {
      checkin_count: 'pages.audience.audienceCreatePage.block.visit.checkin-count.label',
      average_checkin_count: 'pages.audience.audienceCreatePage.block.visit.average-checkin.label',
      last_valid_checkin: 'pages.audience.audienceCreatePage.block.visit.last-checkin.label',
      days_since_register: 'pages.audience.audienceCreatePage.block.visit.days-since-registration.label'
    }
    const isFeminine = feminineMap[rule.attribute] ? 'feminine.' : ''
    const operatorKey = `pages.audience.operators.${isFeminine}${rule.operator}`

    let suffix = ''
    if (['last_valid_checkin', 'days_since_register'].includes(rule.attribute)) {
      suffix = ` ${t('global.words.days')}`
    }

    const attributeText = attributeMap[rule.attribute] ? t(attributeMap[rule.attribute]) : rule.attribute
    const operatorText = t(operatorKey, { fallbackWarn: false }) || rule.operator

    return `${attributeText} ${operatorText} ${rule.value}${suffix}`
  }

  const description = (frequency: FrequencyType): string => {
    if (!frequency.rules?.length) {
      return ''
    }

    return frequency.rules
      .map((rule) => formattedFrequencyRule(rule))
      .join('. ')
      .concat('.')
  }

  const getSegmentIdFromFrequencyTypeSegments = (frequencyType: string): number => {
    return frequencyTypesSegments.value.find(
      (segment: any) => segment.type === 'dynamic' && segment.frequency_type === frequencyType
    )?.id
  }

  return {
    getBgColorBySlug,
    getHexaColorBySlug,
    description,
    bgColors,
    hexaColors,
    getSegmentIdFromFrequencyTypeSegments
  }
}
